import {
  Material,
  Mesh,
  Object3D,
} from "three";

interface FadedMeshState {
  readonly originalMaterial: Material | Material[];
  readonly fadedMaterials: readonly Material[];
}

interface ObstructionTarget {
  readonly registration: symbol;
  readonly meshes: readonly Mesh[];
}

function materialsOf(
  material: Material | readonly Material[],
): readonly Material[] {
  return Array.isArray(material) ? material : [material as Material];
}

function collectMeshes(object: Object3D): readonly Mesh[] {
  const meshes: Mesh[] = [];
  object.traverse((child) => {
    if (child instanceof Mesh) {
      meshes.push(child);
    }
  });
  return meshes;
}

/**
 * Clones material instances before fading, so shared world materials are never
 * mutated. Restoring or disposing this controller always puts the exact
 * original material references back on every live mesh.
 */
export class ObstructionFadeController {
  readonly #targets = new Map<string, ObstructionTarget>();
  readonly #meshesByObject = new WeakMap<Object3D, readonly Mesh[]>();
  readonly #activeIds = new Set<string>();
  readonly #ignoredIds = new Set<string>();
  readonly #fadedMeshes = new Map<Mesh, FadedMeshState>();
  readonly #fadedOpacity: number;
  #disposed = false;

  constructor(fadedOpacity: number) {
    if (
      !Number.isFinite(fadedOpacity) ||
      fadedOpacity < 0 ||
      fadedOpacity > 1
    ) {
      throw new Error("fadedOpacity must be between zero and one");
    }
    this.#fadedOpacity = fadedOpacity;
  }

  register(id: string, object: Object3D): () => void {
    this.#assertUsable();
    if (id.trim().length === 0) {
      throw new Error("Camera obstruction ID must not be empty");
    }
    const registration = Symbol(id);
    let meshes = this.#meshesByObject.get(object);
    if (meshes === undefined) {
      meshes = collectMeshes(object);
      this.#meshesByObject.set(object, meshes);
    }
    this.#targets.set(id, {
      registration,
      meshes,
    });
    if (this.#activeIds.has(id) && !this.#ignoredIds.has(id)) {
      this.#reconcile();
    }
    let registered = true;
    return () => {
      if (!registered) {
        return;
      }
      registered = false;
      if (this.#targets.get(id)?.registration === registration) {
        this.unregister(id);
      }
    };
  }

  unregister(id: string): void {
    if (this.#disposed) {
      return;
    }
    this.#targets.delete(id);
    const wasActive = this.#activeIds.delete(id);
    if (wasActive) this.#reconcile();
  }

  setObstructedIds(ids: readonly string[]): void {
    if (this.#disposed) {
      return;
    }
    this.#activeIds.clear();
    for (const id of ids) {
      if (this.#targets.has(id) && !this.#ignoredIds.has(id)) {
        this.#activeIds.add(id);
      }
    }
    this.#reconcile();
  }

  setIgnoredIds(ids: readonly string[]): void {
    if (this.#disposed) {
      return;
    }
    this.#ignoredIds.clear();
    for (const id of ids) {
      this.#ignoredIds.add(id);
      this.#activeIds.delete(id);
    }
    this.#reconcile();
  }

  get fadedMeshCount(): number {
    return this.#fadedMeshes.size;
  }

  dispose(): void {
    if (this.#disposed) {
      return;
    }
    for (const mesh of [...this.#fadedMeshes.keys()]) {
      this.#restore(mesh);
    }
    this.#targets.clear();
    this.#activeIds.clear();
    this.#ignoredIds.clear();
    this.#disposed = true;
  }

  #reconcile(): void {
    const desiredMeshes = new Set<Mesh>();
    for (const id of this.#activeIds) {
      const target = this.#targets.get(id);
      if (target === undefined) {
        continue;
      }
      for (const mesh of target.meshes) {
        desiredMeshes.add(mesh);
      }
    }

    for (const mesh of desiredMeshes) {
      if (!this.#fadedMeshes.has(mesh)) {
        this.#fade(mesh);
      }
    }
    for (const mesh of [...this.#fadedMeshes.keys()]) {
      if (!desiredMeshes.has(mesh)) {
        this.#restore(mesh);
      }
    }
  }

  #fade(mesh: Mesh): void {
    const originalMaterial = mesh.material;
    const fadedMaterials = materialsOf(originalMaterial).map((material) => {
      const faded = material.clone();
      faded.transparent = true;
      faded.opacity = Math.min(material.opacity, this.#fadedOpacity);
      faded.depthWrite = false;
      // Obstruction fading is a camera aid, not a physically layered glass
      // surface. Three otherwise renders transparent DoubleSide materials in
      // separate back- and front-face passes, which can almost double the
      // central-city draw count the first time the camera enters a facade.
      faded.forceSinglePass = true;
      faded.needsUpdate = true;
      return faded;
    });
    mesh.material = Array.isArray(originalMaterial)
      ? fadedMaterials
      : fadedMaterials[0]!;
    this.#fadedMeshes.set(mesh, {
      originalMaterial,
      fadedMaterials,
    });
  }

  #restore(mesh: Mesh): void {
    const state = this.#fadedMeshes.get(mesh);
    if (state === undefined) {
      return;
    }
    mesh.material = state.originalMaterial;
    for (const material of state.fadedMaterials) {
      material.dispose();
    }
    this.#fadedMeshes.delete(mesh);
  }

  #assertUsable(): void {
    if (this.#disposed) {
      throw new Error("ObstructionFadeController has been disposed");
    }
  }
}
